import { Capacitor, registerPlugin } from '@capacitor/core'
import {
  AndroidBiometryStrength,
  BiometricAuth,
  BiometryErrorType
} from '@aparajita/capacitor-biometric-auth'
import {
  appendSecurityPinDigit,
  MAX_SECURITY_PIN_LENGTH,
  normalizeSecurityPinDigits,
  removeSecurityPinDigit
} from './mobile-security-pin-input.js'

const PREFS_KEY = 'kifaka_mobile_security_prefs_v1';
const SESSION_KEY = 'kifaka_mobile_security_session_v1';
const MAX_FAILED_ATTEMPTS = 5;
const MIN_PIN_LENGTH = 4;
const MAX_PIN_LENGTH = MAX_SECURITY_PIN_LENGTH;
// Keep mobile PIN prompts out of normal in-app navigation. We only relock
// after the app has stayed hidden long enough to behave like a real leave/lock.
const BACKGROUND_RELOCK_DELAY_MS = 1500;
// Native Android screen wake events are not always forwarded reliably to the
// WebView lifecycle. Keep a light foreground poll so relock after a real phone
// screen lock is enforced, without re-triggering during regular in-app routing.
const NATIVE_SCREEN_LOCK_POLL_MS = 1000;

let activeUnlockPromise = null;
let nativeScreenLockPlugin = null;

function getNativeScreenLockPlugin() {
  if (nativeScreenLockPlugin !== null) return nativeScreenLockPlugin;
  try {
    nativeScreenLockPlugin = registerPlugin('ScreenLock');
  } catch (_error) {
    nativeScreenLockPlugin = null;
  }
  return nativeScreenLockPlugin;
}

async function getNativeScreenLockState() {
  const nativePlugin = getNativeScreenLockPlugin();
  if (!nativePlugin?.getState) {
    return { shouldRequireUnlock: false, action: '' };
  }
  try {
    return await nativePlugin.getState();
  } catch (_error) {
    return { shouldRequireUnlock: false, action: '' };
  }
}

function clearNativeScreenLockFlag() {
  const nativePlugin = getNativeScreenLockPlugin();
  if (!nativePlugin?.clearUnlockFlag) return;
  Promise.resolve(nativePlugin.clearUnlockFlag()).catch(() => {});
}

function isNativeBiometricRuntime() {
  try {
    return Boolean(Capacitor?.isNativePlatform?.());
  } catch (_error) {
    return false;
  }
}

function getDefaultPrefs() {
  return {
    enabled: false,
    accountId: '',
    pinHash: '',
    biometricEnabled: false,
    biometricCredentialId: '',
    failedAttempts: 0,
    updatedAt: null
  };
}

function getDefaultSession() {
  return {
    accountId: '',
    unlocked: false,
    hiddenAt: 0,
    updatedAt: 0
  };
}

function safeStorage(type) {
  try {
    return type === 'session' ? window.sessionStorage : window.localStorage;
  } catch (_error) {
    return null;
  }
}

function readJson(type, key, fallback) {
  const storage = safeStorage(type);
  if (!storage) return fallback;
  try {
    const raw = storage.getItem(key);
    if (!raw) return fallback;
    return { ...fallback, ...(JSON.parse(raw) || {}) };
  } catch (_error) {
    return fallback;
  }
}

function writeJson(type, key, value) {
  const storage = safeStorage(type);
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // noop
  }
}

function removeKey(type, key) {
  const storage = safeStorage(type);
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch (_error) {
    // noop
  }
}

export function resolveMobileAsset(relativePath) {
  const path = String(relativePath || '').replace(/^\.?\//, '');
  const pagePath = String(window.location?.pathname || '');
  return pagePath.includes('/pages/webpages/html/')
    ? `../../../${path}`
    : `./${path}`;
}

export function getSecurityPreferences() {
  return readJson('local', PREFS_KEY, getDefaultPrefs());
}

export function saveSecurityPreferences(nextPrefs) {
  const merged = {
    ...getDefaultPrefs(),
    ...getSecurityPreferences(),
    ...(nextPrefs || {}),
    updatedAt: new Date().toISOString()
  };
  writeJson('local', PREFS_KEY, merged);
  return merged;
}

export function clearSecurityPreferences() {
  removeKey('local', PREFS_KEY);
}

export function getSecuritySession() {
  return readJson('session', SESSION_KEY, getDefaultSession());
}

export function saveSecuritySession(nextSession) {
  const merged = {
    ...getDefaultSession(),
    ...getSecuritySession(),
    ...(nextSession || {}),
    updatedAt: Date.now()
  };
  writeJson('session', SESSION_KEY, merged);
  return merged;
}

export function clearSecuritySession() {
  removeKey('session', SESSION_KEY);
}

export function normalizeSecurityForAccount(accountId) {
  const normalizedAccountId = String(accountId || '').trim();
  const prefs = getSecurityPreferences();
  if (!prefs.enabled) return prefs;
  if (!normalizedAccountId) return prefs;
  if (!prefs.accountId || prefs.accountId === normalizedAccountId) return prefs;

  clearSecurityPreferences();
  clearSecuritySession();
  return getDefaultPrefs();
}

export function isSecurityEnabledForAccount(accountId) {
  const normalizedAccountId = String(accountId || '').trim();
  const prefs = getSecurityPreferences();
  return Boolean(
    prefs.enabled
    && prefs.pinHash
    && normalizedAccountId
    && prefs.accountId === normalizedAccountId
  );
}

export function isSecurityUnlockedForAccount(accountId) {
  const normalizedAccountId = String(accountId || '').trim();
  const session = getSecuritySession();
  return Boolean(session.unlocked && normalizedAccountId && session.accountId === normalizedAccountId);
}

export function markSecurityUnlocked(accountId) {
  const normalizedAccountId = String(accountId || '').trim();
  saveSecuritySession({
    accountId: normalizedAccountId,
    unlocked: true,
    hiddenAt: 0
  });
  clearNativeScreenLockFlag();
  resetSecurityFailures(normalizedAccountId);
}

export function markSecurityLocked(accountId) {
  const normalizedAccountId = String(accountId || '').trim();
  saveSecuritySession({
    accountId: normalizedAccountId,
    unlocked: false,
    hiddenAt: Date.now()
  });
}

export function shouldRequireSecurityUnlock(accountId) {
  const normalizedAccountId = String(accountId || '').trim();
  if (!isSecurityEnabledForAccount(normalizedAccountId)) return false;
  return !isSecurityUnlockedForAccount(normalizedAccountId);
}

export function isValidPinFormat(pin) {
  return new RegExp(`^\\d{${MIN_PIN_LENGTH},${MAX_PIN_LENGTH}}$`).test(String(pin || '').trim());
}

function bufferToHex(buffer) {
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

function randomBytes(length = 32) {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return bytes;
}

function bufferToBase64Url(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlToBytes(value) {
  const normalized = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
  const binary = window.atob(normalized + padding);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

export async function hashPin(pin) {
  const encoder = new TextEncoder();
  const digest = await window.crypto.subtle.digest('SHA-256', encoder.encode(String(pin || '').trim()));
  return bufferToHex(digest);
}

export async function configureLocalSecurity({
  accountId,
  pin,
  biometricEnabled = false,
  biometricCredentialId = ''
}) {
  const normalizedAccountId = String(accountId || '').trim();
  if (!normalizedAccountId) throw new Error('Compte introuvable.');
  if (!isValidPinFormat(pin)) throw new Error('Le code doit contenir 4 a 6 chiffres.');

  const prefs = saveSecurityPreferences({
    enabled: true,
    accountId: normalizedAccountId,
    pinHash: await hashPin(pin),
    biometricEnabled: Boolean(biometricEnabled && biometricCredentialId),
    biometricCredentialId: String(biometricCredentialId || '').trim(),
    failedAttempts: 0
  });
  markSecurityUnlocked(normalizedAccountId);
  return prefs;
}

export async function verifyLocalPin({ accountId, pin }) {
  const normalizedAccountId = String(accountId || '').trim();
  const prefs = getSecurityPreferences();
  if (!prefs.enabled || prefs.accountId !== normalizedAccountId || !prefs.pinHash) return false;
  return prefs.pinHash === await hashPin(pin);
}

export function resetSecurityFailures(accountId) {
  const normalizedAccountId = String(accountId || '').trim();
  const prefs = getSecurityPreferences();
  if (!prefs.accountId || prefs.accountId !== normalizedAccountId) return prefs;
  return saveSecurityPreferences({ failedAttempts: 0 });
}

export function registerSecurityFailure(accountId) {
  const normalizedAccountId = String(accountId || '').trim();
  const prefs = getSecurityPreferences();
  if (!prefs.accountId || prefs.accountId !== normalizedAccountId) {
    return { count: 0, exhausted: false, remaining: MAX_FAILED_ATTEMPTS };
  }
  const count = Math.max(0, Number(prefs.failedAttempts) || 0) + 1;
  saveSecurityPreferences({ failedAttempts: count });
  return {
    count,
    exhausted: count >= MAX_FAILED_ATTEMPTS,
    remaining: Math.max(0, MAX_FAILED_ATTEMPTS - count)
  };
}

export function disableLocalSecurity() {
  clearSecurityPreferences();
  clearSecuritySession();
}

export async function isBiometricAvailable() {
  if (isNativeBiometricRuntime()) {
    try {
      const info = await BiometricAuth.checkBiometry();
      return Boolean(info?.isAvailable);
    } catch (_error) {
      return false;
    }
  }
  if (typeof window === 'undefined') return false;
  if (!window.isSecureContext) return false;
  if (!window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (_error) {
    return false;
  }
}

export async function enrollBiometricCredential(accountId) {
  const normalizedAccountId = String(accountId || '').trim();
  if (!normalizedAccountId) throw new Error('Compte introuvable.');

  if (isNativeBiometricRuntime()) {
    let info = null;
    try {
      info = await BiometricAuth.checkBiometry();
    } catch (_error) {
      info = null;
    }
    if (!info?.isAvailable) {
      throw new Error(info?.reason || 'La biometrie n est pas disponible sur cet appareil.');
    }

    await BiometricAuth.authenticate({
      reason: 'Confirmez votre biometrie pour activer le deverrouillage local.',
      cancelTitle: 'Annuler',
      allowDeviceCredential: false,
      iosFallbackTitle: '',
      androidTitle: 'Activer la biometrie',
      androidSubtitle: 'Kifaka app',
      androidConfirmationRequired: false,
      androidBiometryStrength: info.strongBiometryIsAvailable
        ? AndroidBiometryStrength.strong
        : AndroidBiometryStrength.weak
    });

    return `native:${normalizedAccountId}`;
  }

  if (!await isBiometricAvailable()) {
    throw new Error('La biometrie n est pas disponible sur cet appareil.');
  }

  const userBytes = new TextEncoder().encode(normalizedAccountId.slice(0, 64));
  const credential = await navigator.credentials.create({
    publicKey: {
      challenge: randomBytes(32),
      rp: {
        name: 'Kifaka',
        id: window.location.hostname || 'localhost'
      },
      user: {
        id: userBytes,
        name: `kifaka-${normalizedAccountId}`,
        displayName: 'Kifaka local unlock'
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 }
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        residentKey: 'preferred'
      },
      timeout: 60000,
      attestation: 'none'
    }
  });

  if (!credential?.rawId) {
    throw new Error('Enrollement biométrique impossible.');
  }

  return bufferToBase64Url(credential.rawId);
}

export async function verifyBiometricUnlock(credentialId) {
  const normalizedCredentialId = String(credentialId || '').trim();
  if (!normalizedCredentialId) return false;

  if (isNativeBiometricRuntime()) {
    let info = null;
    try {
      info = await BiometricAuth.checkBiometry();
    } catch (_error) {
      info = null;
    }
    if (!info?.isAvailable) {
      throw new Error(info?.reason || 'Biometrie indisponible sur cet appareil.');
    }

    await BiometricAuth.authenticate({
      reason: 'Deverrouillez Kifaka app.',
      cancelTitle: 'Annuler',
      allowDeviceCredential: true,
      iosFallbackTitle: 'Utiliser le code',
      androidTitle: 'Deverrouiller',
      androidSubtitle: 'Kifaka app',
      androidConfirmationRequired: false,
      androidBiometryStrength: info.strongBiometryIsAvailable
        ? AndroidBiometryStrength.strong
        : AndroidBiometryStrength.weak
    });
    return true;
  }

  if (!await isBiometricAvailable()) return false;

  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge: randomBytes(32),
      allowCredentials: [{
        id: base64UrlToBytes(normalizedCredentialId),
        type: 'public-key'
      }],
      userVerification: 'required',
      timeout: 45000
    }
  });

  return Boolean(assertion);
}

export async function forceLocalLogout({
  supabase,
  redirectTo = 'mobile-login.html',
  resetSecurity = false
} = {}) {
  clearSecuritySession();
  if (resetSecurity) {
    clearSecurityPreferences();
  }
  try {
    await supabase?.auth?.signOut?.();
  } catch (_error) {
    // noop
  }
  window.location.replace(redirectTo);
}

function createUnlockOverlay() {
  const existing = document.getElementById('kifakaSecurityOverlay');
  if (existing) return existing;
  const biometricIconSrc = resolveMobileAsset('assets/mobile/biometric-fingerprint.png');

  const overlay = document.createElement('div');
  overlay.id = 'kifakaSecurityOverlay';
  overlay.className = 'kifaka-security-overlay';
  overlay.hidden = true;
  overlay.innerHTML = `
    <div class="kifaka-security-backdrop"></div>
    <div class="kifaka-security-card" role="dialog" aria-modal="true" aria-labelledby="kifakaSecurityTitle">
      <div class="kifaka-security-shell">
        <div class="kifaka-security-copy-block">
          <h2 id="kifakaSecurityTitle">Bonjour</h2>
          <p id="kifakaSecurityCopy" class="kifaka-security-copy">Entrez votre code pour vous connecter</p>
        </div>
        <div id="kifakaSecurityDots" class="kifaka-security-dots" aria-hidden="true">
          <span class="kifaka-security-dot"></span>
          <span class="kifaka-security-dot"></span>
          <span class="kifaka-security-dot"></span>
          <span class="kifaka-security-dot"></span>
          <span class="kifaka-security-dot"></span>
          <span class="kifaka-security-dot"></span>
        </div>
        <p id="kifakaSecurityError" class="kifaka-security-error" hidden></p>
        <input id="kifakaSecurityInput" class="kifaka-security-hidden-input" type="text" inputmode="none" autocomplete="off" maxlength="${MAX_PIN_LENGTH}" tabindex="-1" />
        <div class="kifaka-security-pad" aria-label="Clavier numerique">
          <button type="button" class="kifaka-security-key" data-key="1">1</button>
          <button type="button" class="kifaka-security-key" data-key="2">2</button>
          <button type="button" class="kifaka-security-key" data-key="3">3</button>
          <button type="button" class="kifaka-security-key" data-key="4">4</button>
          <button type="button" class="kifaka-security-key" data-key="5">5</button>
          <button type="button" class="kifaka-security-key" data-key="6">6</button>
          <button type="button" class="kifaka-security-key" data-key="7">7</button>
          <button type="button" class="kifaka-security-key" data-key="8">8</button>
          <button type="button" class="kifaka-security-key" data-key="9">9</button>
          <button id="kifakaSecurityBiometric" type="button" class="kifaka-security-key kifaka-security-key-icon kifaka-security-key-bio" aria-label="Utiliser la biometrie">
            <img src="${biometricIconSrc}" class="kifaka-security-biometric-image" alt="" aria-hidden="true" />
          </button>
          <button type="button" class="kifaka-security-key" data-key="0">0</button>
          <button id="kifakaSecurityBackspace" type="button" class="kifaka-security-key kifaka-security-key-icon" aria-label="Effacer le dernier chiffre" title="Effacer le dernier chiffre">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9.2 5.6H19.2C20.19 5.6 21 6.41 21 7.4V16.6C21 17.59 20.19 18.4 19.2 18.4H9.2L3 12L9.2 5.6Z" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round"/>
              <path d="M12 9.2L16.2 14.8M16.2 9.2L12 14.8" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <button id="kifakaSecurityForgot" type="button" class="kifaka-security-forgot">Déconnection</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  return overlay;
}

export async function requestSecurityUnlock({
  accountId,
  supabase,
  redirectTo = 'mobile-login.html',
  displayName = ''
} = {}) {
  const normalizedAccountId = String(accountId || '').trim();
  if (!shouldRequireSecurityUnlock(normalizedAccountId)) return true;
  if (activeUnlockPromise) return activeUnlockPromise;

  const prefs = getSecurityPreferences();
  const overlay = createUnlockOverlay();
  const input = overlay.querySelector('#kifakaSecurityInput');
  const errorBox = overlay.querySelector('#kifakaSecurityError');
  const biometricButton = overlay.querySelector('#kifakaSecurityBiometric');
  const backspaceButton = overlay.querySelector('#kifakaSecurityBackspace');
  const forgotButton = overlay.querySelector('#kifakaSecurityForgot');
  const keyButtons = Array.from(overlay.querySelectorAll('[data-key]'));
  const dots = Array.from(overlay.querySelectorAll('.kifaka-security-dot'));
  const title = overlay.querySelector('#kifakaSecurityTitle');
  const copy = overlay.querySelector('#kifakaSecurityCopy');
  let pinValue = '';
  let busy = false;

  overlay.hidden = false;
  document.body.classList.add('kifaka-security-locked');
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
  if (title) {
    const normalizedDisplayName = String(displayName || '').trim();
    title.textContent = normalizedDisplayName ? `Bonjour ${normalizedDisplayName}` : 'Bonjour';
  }
  if (copy) {
    copy.textContent = 'Entrez votre code pour vous connecter';
  }

  const showError = (message) => {
    if (!errorBox) return;
    if (!message) {
      errorBox.hidden = true;
      errorBox.textContent = '';
      return;
    }
    errorBox.hidden = false;
    errorBox.textContent = message;
  };

  const syncDots = () => {
    dots.forEach((dot, index) => {
      dot.classList.toggle('is-filled', index < pinValue.length);
    });
    if (input) input.value = pinValue;
  };

  const setPinValue = (nextValue) => {
    pinValue = normalizeSecurityPinDigits(nextValue);
    syncDots();
  };

  const describeBiometricError = (error) => {
    const code = String(error?.code || '').trim();
    if (!code || code === BiometryErrorType.userCancel || code === BiometryErrorType.systemCancel || code === BiometryErrorType.appCancel) {
      return '';
    }
    if (code === BiometryErrorType.biometryLockout) return 'Biometrie temporairement verrouillee. Utilisez votre code.';
    if (code === BiometryErrorType.biometryNotEnrolled) return 'Ajoutez une empreinte digitale ou Face ID sur l appareil.';
    if (code === BiometryErrorType.biometryNotAvailable) return 'Biometrie indisponible sur cet appareil.';
    if (code === BiometryErrorType.passcodeNotSet || code === BiometryErrorType.noDeviceCredential) {
      return 'Activez la securite du telephone pour utiliser la biometrie.';
    }
    return `Biometrie indisponible: ${error?.message || error}`;
  };

  const canUseBiometric = Boolean(prefs.biometricEnabled && prefs.biometricCredentialId && await isBiometricAvailable());
  if (biometricButton) {
    biometricButton.classList.toggle('kifaka-security-key-placeholder', !canUseBiometric);
    biometricButton.disabled = !canUseBiometric;
    biometricButton.setAttribute('aria-hidden', canUseBiometric ? 'false' : 'true');
  }

  syncDots();

  activeUnlockPromise = new Promise((resolve) => {
    let resolved = false;

    const finish = (result) => {
      if (resolved) return;
      resolved = true;
      overlay.hidden = true;
      document.body.classList.remove('kifaka-security-locked');
      showError('');
      setPinValue('');
      biometricButton?.removeEventListener('click', onBiometric);
      backspaceButton?.removeEventListener('click', onBackspace);
      forgotButton?.removeEventListener('click', onForgot);
      keyButtons.forEach((button) => button.removeEventListener('click', onDigit));
      document.removeEventListener('keydown', onKeyDown, true);
      activeUnlockPromise = null;
      resolve(result);
    };

    const onSuccess = () => {
      markSecurityUnlocked(normalizedAccountId);
      finish(true);
    };

    const onFailure = async () => {
      const failure = registerSecurityFailure(normalizedAccountId);
      if (failure.exhausted) {
        showError('Trop de tentatives. Deconnexion du compte.');
        await forceLocalLogout({ supabase, redirectTo, resetSecurity: true });
        finish(false);
        return;
      }
      showError(`Code incorrect. ${failure.remaining} tentative(s) restante(s).`);
      setPinValue('');
    };

    const attemptPinUnlock = async (forceDecision = false) => {
      if (busy) return;
      if (pinValue.length < MIN_PIN_LENGTH) {
        if (forceDecision) {
          showError('Entrez un code de 4 a 6 chiffres.');
        }
        return;
      }
      busy = true;
      let valid = false;
      try {
        valid = await verifyLocalPin({ accountId: normalizedAccountId, pin: pinValue });
      } finally {
        busy = false;
      }
      if (valid) {
        onSuccess();
        return;
      }
      if (forceDecision || pinValue.length >= MAX_PIN_LENGTH) {
        await onFailure();
      }
    };

    const onDigit = async (event) => {
      const nextDigit = String(event.currentTarget?.getAttribute('data-key') || '').trim();
      const nextPinValue = appendSecurityPinDigit(pinValue, nextDigit);
      if (busy || nextPinValue === pinValue) return;
      event.preventDefault();
      event.stopPropagation();
      showError('');
      setPinValue(nextPinValue);
      await attemptPinUnlock(false);
    };

    const onBackspace = (event) => {
      const nextPinValue = removeSecurityPinDigit(pinValue);
      if (busy || nextPinValue === pinValue) return;
      event?.preventDefault();
      event?.stopPropagation();
      showError('');
      setPinValue(nextPinValue);
    };

    const onKeyDown = async (event) => {
      if (resolved) return;
      const key = String(event.key || '');
      if (/^\d$/.test(key)) {
        event.preventDefault();
        const nextPinValue = appendSecurityPinDigit(pinValue, key);
        if (busy || nextPinValue === pinValue) return;
        showError('');
        setPinValue(nextPinValue);
        await attemptPinUnlock(false);
        return;
      }
      if (key === 'Backspace' || key === 'Delete') {
        event.preventDefault();
        onBackspace();
        return;
      }
      if (key === 'Enter') {
        event.preventDefault();
        await attemptPinUnlock(true);
      }
    };

    const onBiometric = async () => {
      try {
        const ok = await verifyBiometricUnlock(prefs.biometricCredentialId);
        if (ok) {
          onSuccess();
          return;
        }
        showError('Verification biometrique indisponible.');
      } catch (error) {
        const message = describeBiometricError(error);
        if (message) showError(message);
      }
    };

    const onForgot = async () => {
      await forceLocalLogout({ supabase, redirectTo, resetSecurity: true });
      finish(false);
    };

    biometricButton?.addEventListener('click', onBiometric);
    backspaceButton?.addEventListener('click', onBackspace);
    forgotButton?.addEventListener('click', onForgot);
    keyButtons.forEach((button) => button.addEventListener('click', onDigit));
    document.addEventListener('keydown', onKeyDown, true);

    // Auto-trigger biometric immediately on overlay open (no button tap needed)
    if (canUseBiometric) {
      window.setTimeout(() => { if (!resolved) onBiometric(); }, 350);
    }
  });

  return activeUnlockPromise;
}

export function bindSecurityLifecycle({
  accountId,
  onUnlockRequired
} = {}) {
  const normalizedAccountId = String(accountId || '').trim();
  if (!normalizedAccountId || typeof onUnlockRequired !== 'function') return () => {};

  // On Android APK we rely on real screen lock events instead of generic
  // backgrounding so the PIN is not re-requested during normal app flow.
  if (isNativeBiometricRuntime()) {
    const nativePlugin = getNativeScreenLockPlugin();
    let nativeHandle = null;
    let cleanupRequested = false;
    const syncTimers = new Set();
    let pollHandle = 0;

    const clearSyncTimers = () => {
      syncTimers.forEach((timerId) => window.clearTimeout(timerId));
      syncTimers.clear();
    };

    const queueVisibleSync = (delayMs = 0) => {
      const timerId = window.setTimeout(() => {
        syncTimers.delete(timerId);
        if (cleanupRequested) return;
        void syncVisibleState();
      }, delayMs);
      syncTimers.add(timerId);
    };

    const syncVisibleState = async () => {
      if (document.hidden) return;
      const nativeState = await getNativeScreenLockState();
      if (nativeState?.shouldRequireUnlock) {
        markSecurityLocked(normalizedAccountId);
      }
      if (shouldRequireSecurityUnlock(normalizedAccountId)) {
        onUnlockRequired();
      }
    };

    const handleNativeEvent = (event = {}) => {
      if (!isSecurityEnabledForAccount(normalizedAccountId)) return;
      const action = String(event?.action || '').trim().toLowerCase();
      if (action === 'screen_off') {
        markSecurityLocked(normalizedAccountId);
        return;
      }
      if (action === 'user_present' || action === 'screen_on') {
        [120, 900, 1800, 3200, 5000].forEach((delayMs) => {
          queueVisibleSync(delayMs);
        });
      }
    };

    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        queueVisibleSync(0);
      }
    };

    const resumeHandler = () => {
      queueVisibleSync(0);
    };

    const focusHandler = () => {
      queueVisibleSync(0);
    };

    document.addEventListener('visibilitychange', visibilityHandler);
    document.addEventListener('resume', resumeHandler, false);
    window.addEventListener('focus', focusHandler);
    window.addEventListener('pageshow', focusHandler);
    pollHandle = window.setInterval(() => {
      if (cleanupRequested || document.hidden) return;
      void syncVisibleState();
    }, NATIVE_SCREEN_LOCK_POLL_MS);
    queueVisibleSync(0);

    if (nativePlugin?.addListener) {
      Promise.resolve(nativePlugin.addListener('screenStateChange', handleNativeEvent))
        .then((handle) => {
          if (cleanupRequested) {
            handle?.remove?.();
            return;
          }
          nativeHandle = handle || null;
        })
        .catch(() => {
          nativeHandle = null;
        });
    }

    return () => {
      cleanupRequested = true;
      clearSyncTimers();
      if (pollHandle) {
        window.clearInterval(pollHandle);
        pollHandle = 0;
      }
      nativeHandle?.remove?.();
      document.removeEventListener('visibilitychange', visibilityHandler);
      document.removeEventListener('resume', resumeHandler, false);
      window.removeEventListener('focus', focusHandler);
      window.removeEventListener('pageshow', focusHandler);
    };
  }

  let hiddenAt = 0;
  let relockTimer = 0;

  const clearRelockTimer = () => {
    if (!relockTimer) return;
    window.clearTimeout(relockTimer);
    relockTimer = 0;
  };

  const handleVisibility = () => {
    if (!isSecurityEnabledForAccount(normalizedAccountId)) return;

    if (document.hidden) {
      hiddenAt = Date.now();
      clearRelockTimer();
      relockTimer = window.setTimeout(() => {
        relockTimer = 0;
        if (!document.hidden) return;
        markSecurityLocked(normalizedAccountId);
      }, BACKGROUND_RELOCK_DELAY_MS);
      return;
    }

    const hiddenDuration = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = 0;
    clearRelockTimer();

    // Only demand the PIN after a real leave/lock interval. This avoids
    // re-prompting during regular page transitions inside the APK.
    if (
      hiddenDuration >= BACKGROUND_RELOCK_DELAY_MS
      && isSecurityUnlockedForAccount(normalizedAccountId)
    ) {
      markSecurityLocked(normalizedAccountId);
    }

    if (shouldRequireSecurityUnlock(normalizedAccountId)) {
      onUnlockRequired();
    }
  };

  document.addEventListener('visibilitychange', handleVisibility);

  return () => {
    clearRelockTimer();
    document.removeEventListener('visibilitychange', handleVisibility);
  };
}
